import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/session';
import { AccountSection } from '@/components/AccountSection';
import { useStore } from '@/state/store';
import { feedbackContext } from '@/data/feedbackContext';
import {
  FEEDBACK_KINDS,
  FEEDBACK_STATUSES,
  listFeedback,
  submitFeedback,
  type FeedbackKind,
  type FeedbackReport,
} from '@/data/feedback';
import './feedback.css';

export function FeedbackPage() {
  const auth = useAuth();
  if (auth.status === 'unconfigured')
    return (
      <div className="feedback-page">
        <h1>Feedback</h1>
        <p>Feedback is not available in this build.</p>
      </div>
    );
  if (auth.status !== 'active' || !auth.userId)
    return (
      <div className="feedback-page">
        <h1>Feedback</h1>
        <p>Sign in with access to send feedback and see your reports.</p>
        <AccountSection />
      </div>
    );
  // A switched account must never inherit another person's report list or draft.
  return <FeedbackForm key={auth.userId} reporter={auth.userId} />;
}

function FeedbackForm({ reporter }: { reporter: string }) {
  const location = useLocation();
  const entry = location.state as { kind?: FeedbackKind; route?: string; bar?: number } | null;
  const [snapshot] = useState(() => ({
    score: useStore.getState().score,
    ref: useStore.getState().currentTuneRef,
  }));
  const [kind, setKind] = useState<FeedbackKind>(
    entry?.kind && entry.kind in FEEDBACK_KINDS ? entry.kind : 'general',
  );
  const [bar, setBar] = useState(entry?.bar?.toString() ?? '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const historyVersion = useRef(0);
  useEffect(() => {
    let current = true;
    const version = ++historyVersion.current;
    const isCurrent = () => current && version === historyVersion.current;
    setLoading(true);
    setHistoryError(null);
    void listFeedback(reporter)
      .then(
        (rows) => {
          if (isCurrent()) setReports(rows);
        },
        (e: unknown) => {
          if (isCurrent())
            setHistoryError(e instanceof Error ? e.message : 'Could not load your reports.');
        },
      )
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [reporter, reload]);
  const context = feedbackContext(
    entry?.route ?? location.pathname,
    kind === 'tune' ? snapshot.score : undefined,
    snapshot.ref,
    bar ? Number(bar) : undefined,
  );
  const send = async () => {
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      const report = await submitFeedback(kind, message, context);
      // A list requested before the insert can arrive afterwards. The RPC
      // result is newer; a stale list must not erase this acknowledgement.
      historyVersion.current += 1;
      setLoading(false);
      setHistoryError(null);
      setReports((rows) => [report, ...rows.filter((r) => r.id !== report.id)]);
      setMessage('');
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send your report. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="feedback-page">
      <h1>Feedback</h1>
      <p>Something wrong, or an idea to share? Send it here and check back for a reply.</p>
      <form
        className="feedback-form"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <label>
          What is it about?
          <select
            aria-label="What is it about?"
            value={kind}
            disabled={busy}
            onChange={(e) => {
              setKind(e.target.value as FeedbackKind);
              setSent(false);
            }}
          >
            {Object.entries(FEEDBACK_KINDS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {kind === 'tune' && (
          <>
            <p className="feedback-tune">
              <strong>{snapshot.score.title || 'Untitled tune'}</strong>
              {snapshot.score.composer ? ` · ${snapshot.score.composer}` : ''}
            </p>
            <label>
              Bar number (optional)
              <input
                className="text-input"
                type="number"
                min={1}
                max={snapshot.score.form.length}
                step={1}
                value={bar}
                disabled={busy}
                onChange={(e) => setBar(e.target.value)}
              />
            </label>
            <p className="muted">
              Use the written bar number, counting a pickup as bar 1. Leave it blank for a problem
              with the whole tune.
            </p>
          </>
        )}
        <label>
          Your message
          <textarea
            className="text-input"
            rows={5}
            maxLength={4000}
            required
            value={message}
            disabled={busy}
            onChange={(e) => {
              setMessage(e.target.value);
              setSent(false);
            }}
            placeholder={
              kind === 'tune'
                ? 'What looks or sounds wrong? What should it be?'
                : 'What happened, or what would you like to improve?'
            }
          />
        </label>
        <p className="muted">
          Your report includes this page, build and browser
          {kind === 'tune' ? ', tune details and the notes and chords in the chosen bar' : ''}. Only
          you and the admin can read it.
        </p>
        {error && (
          <p role="alert" className="home-load-error">
            {error}
          </p>
        )}
        {sent && (
          <p role="status">
            Thanks — your report was sent. Its status and any reply will appear below.
          </p>
        )}
        <button className="btn btn-primary" type="submit" disabled={busy || !message.trim()}>
          {busy ? 'Sending…' : 'Send feedback'}
        </button>
      </form>
      <section aria-labelledby="my-feedback-heading">
        <div className="feedback-heading">
          <h2 id="my-feedback-heading">Your reports</h2>
          <button className="btn btn-sm" disabled={loading} onClick={() => setReload((n) => n + 1)}>
            Refresh reports
          </button>
        </div>
        {historyError && (
          <p role="alert" className="home-load-error">
            Could not load your reports: {historyError}
          </p>
        )}
        {loading && <p className="muted">Loading reports…</p>}
        {!loading && !historyError && reports.length === 0 && (
          <p className="muted">No reports yet.</p>
        )}
        {reports.map((report) => (
          <article key={report.id} className="feedback-report">
            <div className="feedback-heading">
              <strong>
                {report.context.tune?.title ?? FEEDBACK_KINDS[report.kind]}
                {report.context.tune?.bar ? ` · bar ${report.context.tune.bar}` : ''}
              </strong>
              <span>{FEEDBACK_STATUSES[report.status]}</span>
            </div>
            <p className="feedback-message">{report.message}</p>
            <small className="muted">{new Date(report.created_at).toLocaleString()}</small>
            {(report.status === 'fixed' || report.status === 'wont_fix') && (
              <p>
                Thanks for helping improve the site.{' '}
                {report.status === 'fixed'
                  ? 'This report has been marked fixed.'
                  : 'The admin has closed this report.'}
              </p>
            )}
            {report.reply && (
              <p className="feedback-reply">
                <strong>Admin reply:</strong> {report.reply}
              </p>
            )}
          </article>
        ))}
        {reports.length >= 200 && <p className="muted">Showing your latest 200 reports.</p>}
      </section>
    </div>
  );
}
